import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, FileSpreadsheet, ListChecks, Printer, Search, X } from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { baixarCsv, carimboArquivo, type ColunaCsv } from '@/lib/csv';
import { imprimirVolumes } from '@/hooks/useImpressaoExpedicao';
import { cn, numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { CabecalhoPagina, Campo, Cartao, LinhasEsqueleto } from '@/componentes/ui/primitivos';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import type { VolumeDanfe } from '@/componentes/expedicao/DanfeSimplificada';

/**
 * Etiquetas de expedição de UMA lista de separação, pedido a pedido.
 *
 * A busca decide o que sai: filtrou uma televenda ou um pedido do
 * marketplace, sai só ele; filtrou um EAN, saem todos os pedidos com aquele
 * EAN. Um botão por etiqueta — a RTM (DANFE resumida, impressão direta) e a
 * do Mercado Livre (PDF num painel na própria tela, sem aba nova): são papéis
 * e impressões diferentes, e juntar num PDF só misturaria os dois.
 */

interface Produto {
  produto: number;
  descricao: string | null;
  unidades: number;
  eans: string[];
}

interface Pedido {
  televenda: number;
  pedidoMarketplace: string | null;
  canal: string;
  estado: string | null;
  nfNumero: string | null;
  nfSerie: string | null;
  etiquetas: number;
  shipmentId: string | null;
  lojaId: number | null;
  produtos: Produto[];
}

interface Lista {
  emissao: number;
  empresa: number | null;
  pedidos: Pedido[];
  /** O hub do ML não respondeu: sem envio, sem etiqueta do ML. */
  hubFora: boolean;
}

type Tipo = 'danfe' | 'meli';

/**
 * Os termos da busca: um por linha, ou separados por espaço, vírgula ou ";".
 * Repetido conta uma vez — a lista colada do Excel vem com o EAN de cada
 * unidade.
 */
function termosDa(busca: string): string[] {
  return [...new Set(busca.split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean))];
}

/** O pedido bate com UM termo? Televenda, pedido do marketplace, nota, EAN ou SKU exatos. */
function bateExato(p: Pedido, t: string): boolean {
  if (String(p.televenda) === t || p.pedidoMarketplace === t || p.nfNumero === t) return true;
  return p.produtos.some(
    (x) => String(x.produto) === t || x.eans.some((e) => e === t || (t.length >= 6 && e.endsWith(t))),
  );
}

/**
 * O pedido bate com a busca?
 *
 * Vários termos (lista colada): bate com QUALQUER um, só por igualdade — com
 * 20 EANs, um pedaço de número casaria pedido que ninguém pediu. Um termo só:
 * também por pedaço da televenda/pedido, e texto procura na descrição.
 */
function bate(p: Pedido, termos: string[]): boolean {
  if (termos.length === 0) return true;
  if (termos.length > 1) return termos.some((t) => bateExato(p, t));
  const t = termos[0]!;
  if (/^\d+$/.test(t)) {
    return bateExato(p, t) || String(p.televenda).includes(t) || (p.pedidoMarketplace ?? '').includes(t);
  }
  const minusculo = t.toLowerCase();
  return p.produtos.some((x) => (x.descricao ?? '').toLowerCase().includes(minusculo));
}

const COLUNAS_EXCEL: ColunaCsv<{ p: Pedido; x: Produto | null }>[] = [
  { cabecalho: 'Lista', valor: () => null },
  { cabecalho: 'Televenda', valor: ({ p }) => p.televenda, texto: true },
  { cabecalho: 'Pedido marketplace', valor: ({ p }) => p.pedidoMarketplace, texto: true },
  { cabecalho: 'Nota fiscal', valor: ({ p }) => p.nfNumero, texto: true },
  { cabecalho: 'Série', valor: ({ p }) => p.nfSerie, texto: true },
  { cabecalho: 'Canal', valor: ({ p }) => p.canal },
  { cabecalho: 'Envio ML', valor: ({ p }) => p.shipmentId, texto: true },
  { cabecalho: 'Conta ML', valor: ({ p }) => p.lojaId, texto: true },
  { cabecalho: 'Estado no ERP', valor: ({ p }) => p.estado },
  { cabecalho: 'SKU', valor: ({ x }) => x?.produto, texto: true },
  { cabecalho: 'Descrição', valor: ({ x }) => x?.descricao },
  { cabecalho: 'EAN', valor: ({ x }) => x?.eans.join(' / '), texto: true },
  { cabecalho: 'Unidades', valor: ({ x }) => x?.unidades },
];

export function EtiquetasLista(): JSX.Element {
  const [params, definirParams] = useSearchParams();
  const emissao = Number(params.get('lista')) || null;
  const [digitada, definirDigitada] = React.useState(emissao ? String(emissao) : '');
  const [busca, definirBusca] = React.useState('');
  const [imprimindo, definirImprimindo] = React.useState<string | null>(null);
  const [erro, definirErro] = React.useState<string | null>(null);
  const [aviso, definirAviso] = React.useState<string | null>(null);
  /** Televendas que o último lote do ML deixou de fora — para filtrar e tentar de novo. */
  const [faltaram, definirFaltaram] = React.useState<number[]>([]);
  /** O PDF do ML aberto no painel da tela. */
  const [pdf, definirPdf] = React.useState<{ url: string; rotulo: string } | null>(null);
  const fecharPdf = (): void =>
    definirPdf((anterior) => {
      if (anterior) URL.revokeObjectURL(anterior.url);
      return null;
    });

  const lista = useQuery<Lista>({
    queryKey: ['etiquetas-lista', emissao],
    queryFn: () => api.get<Lista>(`/api/etiquetas-lista/${emissao}`),
    enabled: emissao !== null,
    staleTime: 5 * 60_000,
  });

  const pedidos = lista.data?.pedidos ?? [];
  const termos = React.useMemo(() => termosDa(busca), [busca]);
  const filtrados = React.useMemo(() => pedidos.filter((p) => bate(p, termos)), [pedidos, termos]);
  /** Termo da lista colada que não achou pedido nenhum — para ninguém achar que saiu. */
  const naoAchados = React.useMemo(
    () => (termos.length > 1 ? termos.filter((t) => !pedidos.some((p) => bateExato(p, t))) : []),
    [termos, pedidos],
  );

  const abrir = (e: React.FormEvent): void => {
    e.preventDefault();
    const n = Number(digitada.trim());
    if (!Number.isInteger(n) || n <= 0) return;
    definirBusca('');
    definirParams({ lista: String(n) });
  };

  async function imprimir(alvo: Pedido[], tipo: Tipo, chave: string): Promise<void> {
    if (!emissao || alvo.length === 0) return;
    definirImprimindo(chave);
    definirErro(null);
    definirAviso(null);
    definirFaltaram([]);
    const televendas = alvo.map((p) => p.televenda);
    const avisos: string[] = [];
    try {
      if (tipo === 'danfe') {
        const r = await api.post<{ etiquetas: { televenda: number; volumes: VolumeDanfe[] }[] }>(
          `/api/etiquetas-lista/${emissao}/danfe`,
          { televendas },
        );
        const volumes = r.etiquetas.flatMap((e) => e.volumes);
        const semDanfe = televendas.length - r.etiquetas.length;
        if (semDanfe > 0) avisos.push(`${numero(semDanfe)} pedido(s) sem dados de DANFE (nota não gerada?).`);
        if (volumes.length > 0) await imprimirVolumes(volumes);
      }

      if (tipo === 'meli') {
        const r = await api.post<{
          impressas: number;
          fora: { televenda: number; motivo: string }[];
          pdf: string | null;
        }>(`/api/etiquetas-lista/${emissao}/meli`, { televendas });
        if (r.pdf) {
          const bytes = Uint8Array.from(atob(r.pdf), (c) => c.charCodeAt(0));
          const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
          // Na própria tela: aba nova escondia a lista e obrigava a voltar.
          definirPdf((anterior) => {
            if (anterior) URL.revokeObjectURL(anterior.url);
            return { url, rotulo: alvo.length === 1 ? `pedido ${alvo[0]!.televenda}` : `${numero(r.impressas)} etiquetas` };
          });
        }
        /*
         * O que SAIU vem primeiro. A primeira versão só falava dos que
         * faltaram, e um lote de 44 com 37 no PDF parecia ter falhado.
         */
        avisos.push(
          r.impressas > 0
            ? `Mercado Livre: ${numero(r.impressas)} ${r.impressas === 1 ? 'etiqueta' : 'etiquetas'} no PDF ao lado.`
            : 'Mercado Livre: nenhuma etiqueta liberada neste filtro.',
        );
        if (r.fora.length > 0) {
          avisos.push(
            `${numero(r.fora.length)} ainda sem etiqueta (o ML não liberou): ` +
              r.fora.map((f) => `${f.televenda} (${f.motivo})`).join('; ') +
              '.',
          );
        }
        definirFaltaram(r.fora.map((f) => f.televenda));
      }
      if (avisos.length > 0) definirAviso(avisos.join(' '));
    } catch (e) {
      definirErro(e instanceof ErroHttp || e instanceof Error ? e.message : 'Não foi possível imprimir.');
    } finally {
      definirImprimindo(null);
    }
  }

  const exportar = (): void => {
    type Linha = { p: Pedido; x: Produto | null };
    const linhas = filtrados.flatMap((p): Linha[] =>
      p.produtos.length === 0 ? [{ p, x: null }] : p.produtos.map((x) => ({ p, x })),
    );
    const colunas = COLUNAS_EXCEL.map((c) =>
      c.cabecalho === 'Lista' ? { ...c, valor: () => emissao, texto: true } : c,
    );
    baixarCsv(`etiquetas-lista-${emissao}-${carimboArquivo()}`, linhas, colunas);
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-4 md:p-6">
      <CabecalhoPagina
        icone={<ListChecks className="h-5 w-5" />}
        sobrelinha="Operação"
        titulo="Etiquetas da lista"
        descricao="Abra a lista de separação, filtre por televenda, pedido do marketplace ou EAN (um ou vários) e imprima a RTM ou a etiqueta do Mercado Livre só do que está filtrado — ou de um pedido pelos botões da linha."
      />

      <Cartao className="flex flex-wrap items-end gap-3 p-4">
        <form onSubmit={abrir} className="flex items-end gap-2">
          <label className="grid gap-1">
            <span className="rotulo">Lista</span>
            <Campo
              value={digitada}
              onChange={(e) => definirDigitada(e.target.value.replace(/\D/g, ''))}
              placeholder="271227"
              inputMode="numeric"
              className="w-32"
            />
          </label>
          <Botao type="submit" variante="secundaria">
            Abrir
          </Botao>
        </form>

        {/* Caixa de várias linhas: num campo de uma linha, a coluna colada do
            Excel chega com as quebras de linha comidas e os EANs grudados. */}
        <label className="grid min-w-[260px] flex-1 gap-1">
          <span className="rotulo flex items-center gap-1">
            <Search className="h-3.5 w-3.5" /> Buscar — um ou vários (cole a coluna do Excel)
          </span>
          <textarea
            value={busca}
            onChange={(e) => definirBusca(e.target.value)}
            placeholder="Televenda, pedido do marketplace, nota fiscal, EAN ou SKU"
            rows={termos.length > 1 ? 4 : 1}
            disabled={!lista.data}
            autoFocus
            className={cn(
              'min-h-toque w-full resize-y rounded-lg border border-borda bg-superficie px-3.5 py-2.5 font-mono text-sm text-texto shadow-xs',
              'placeholder:font-sans placeholder:text-textoSuave hover:border-bordaForte',
              'focus:border-primaria focus:outline-none focus:ring-4 focus:ring-primaria/12',
              'disabled:cursor-not-allowed disabled:bg-superficie2 disabled:opacity-60',
            )}
          />
        </label>

        <div className="flex gap-2">
          {(['danfe', 'meli'] as const).map((t) => (
            <Botao
              key={t}
              variante="primaria"
              disabled={!lista.data || filtrados.length === 0 || imprimindo !== null}
              carregando={imprimindo === `lote-${t}`}
              onClick={() => void imprimir(filtrados, t, `lote-${t}`)}
              title={termos.length > 0 ? 'Imprime todos os pedidos do filtro' : 'Sem filtro: imprime a lista inteira'}
            >
              <Printer className="h-4 w-4" />
              Imprimir lote {t === 'danfe' ? 'RTM' : 'Mercado Livre'} ({numero(filtrados.length)})
            </Botao>
          ))}
          <Botao variante="secundaria" disabled={!lista.data || filtrados.length === 0} onClick={exportar}>
            <FileSpreadsheet className="h-4 w-4" />
            Excel
          </Botao>
        </div>
      </Cartao>

      {lista.data?.hubFora ? (
        <p className="flex items-center gap-2 rounded-lg border border-status-atencao/30 bg-status-atencaoFundo px-3 py-2 text-[13px]">
          <AlertTriangle className="h-4 w-4 text-status-atencao" />
          O hub do Mercado Livre não respondeu: a etiqueta do ML está indisponível agora. A DANFE sai normalmente.
        </p>
      ) : null}
      {naoAchados.length > 0 ? (
        <p className="rounded-lg border border-status-atencao/30 bg-status-atencaoFundo px-3 py-2 text-[13px]">
          {numero(naoAchados.length)} {naoAchados.length === 1 ? 'termo não achou' : 'termos não acharam'} pedido nesta lista:{' '}
          <span className="font-mono">{naoAchados.join(', ')}</span>
        </p>
      ) : null}
      {erro ? (
        <p className="flex items-center gap-2 rounded-lg border border-status-divergencia/30 bg-status-divergenciaFundo px-3 py-2 text-[13px] font-semibold text-status-divergencia">
          <AlertTriangle className="h-4 w-4" /> {erro}
        </p>
      ) : null}
      {aviso ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-status-atencao/30 bg-status-atencaoFundo px-3 py-2 text-[13px]">
          <p className="min-w-0 flex-1">{aviso}</p>
          {faltaram.length > 0 ? (
            <Botao
              tamanho="pequeno"
              variante="secundaria"
              onClick={() => {
                definirBusca(faltaram.join(' '));
                definirFaltaram([]);
              }}
            >
              Filtrar os {numero(faltaram.length)} que faltaram
            </Botao>
          ) : null}
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 gap-4">
      <Cartao className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {emissao === null ? (
          <EstadoVazio titulo="Informe o número da lista" detalhe="O número da TELEVENDAS_EMISSAO, ex.: 271227." />
        ) : lista.isLoading ? (
          <div className="p-4">
            <LinhasEsqueleto linhas={8} />
          </div>
        ) : lista.isError ? (
          <EstadoErro erro={lista.error} aoTentarDeNovo={() => void lista.refetch()} />
        ) : (
          <>
            <p className="border-b border-borda px-4 py-2 text-[12.5px] text-textoFraco">
              Lista <b className="text-texto">{lista.data!.emissao}</b> · empresa {lista.data!.empresa} ·{' '}
              {numero(filtrados.length)} de {numero(pedidos.length)} pedidos
              {termos.length > 1 ? ` · ${numero(termos.length)} termos buscados` : ''}
            </p>
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="w-full text-[13px]">
                <thead className="sticky top-0 z-10 bg-superficie2 text-left text-[12px] text-textoFraco">
                  <tr>
                    <th className="px-3 py-2">Televenda</th>
                    <th className="px-3 py-2">Pedido marketplace</th>
                    <th className="px-3 py-2">Nota fiscal</th>
                    <th className="px-3 py-2">Produto</th>
                    <th className="px-3 py-2">EAN</th>
                    <th className="px-3 py-2 text-right">Etiq.</th>
                    <th className="px-3 py-2">Estado</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map((p) => (
                    <tr key={p.televenda} className="border-t border-borda align-top hover:bg-superficie2/60">
                      <td className="tabular px-3 py-2 font-mono font-semibold">{p.televenda}</td>
                      <td className="tabular px-3 py-2 font-mono">
                        {p.pedidoMarketplace ?? '—'}
                        {p.canal === 'meli' && !p.shipmentId && !lista.data!.hubFora ? (
                          <span className="block text-[11px] text-status-atencao">sem envio no hub</span>
                        ) : null}
                      </td>
                      <td className="tabular px-3 py-2 font-mono">
                        {p.nfNumero ? (
                          <>
                            {p.nfNumero}
                            {p.nfSerie ? <span className="text-textoFraco"> / {p.nfSerie}</span> : null}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {p.produtos.map((x) => (
                          <div key={x.produto}>
                            <span className="font-mono">{x.produto}</span> — {x.descricao}
                            {x.unidades > 1 ? <b> ×{x.unidades}</b> : null}
                          </div>
                        ))}
                      </td>
                      <td className="tabular px-3 py-2 font-mono text-[12px]">
                        {p.produtos.map((x) => (
                          <div key={x.produto}>{x.eans[0] ?? '—'}</div>
                        ))}
                      </td>
                      <td className="tabular px-3 py-2 text-right">{p.etiquetas}</td>
                      <td className="px-3 py-2 text-textoFraco">{p.estado ?? '—'}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right">
                        {(['danfe', 'meli'] as const).map((t) => (
                          <Botao
                            key={t}
                            tamanho="micro"
                            variante="suave"
                            disabled={imprimindo !== null}
                            carregando={imprimindo === `${t}-${p.televenda}`}
                            onClick={() => void imprimir([p], t, `${t}-${p.televenda}`)}
                            className="ml-1.5"
                          >
                            <Printer className="h-3.5 w-3.5" />
                            {t === 'danfe' ? 'RTM' : 'ML'}
                          </Botao>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtrados.length === 0 ? (
                <EstadoVazio titulo="Nenhum pedido bate com a busca" detalhe="Confira a televenda, o pedido ou o EAN." />
              ) : null}
            </div>
          </>
        )}
      </Cartao>

      {/* O PDF do ML ao lado da lista: imprime pelo botão do visualizador e
          a lista continua à vista para o próximo filtro. */}
      {pdf ? (
        <Cartao className="flex min-h-0 w-[46%] shrink-0 flex-col overflow-hidden">
          <div className="flex items-center justify-between gap-2 border-b border-borda px-4 py-2 text-[12.5px]">
            <span>
              Etiqueta Mercado Livre · <b className="text-texto">{pdf.rotulo}</b>
            </span>
            <Botao tamanho="micro" variante="fantasma" onClick={fecharPdf}>
              <X className="h-3.5 w-3.5" />
              Fechar
            </Botao>
          </div>
          <iframe title="Etiqueta Mercado Livre" src={pdf.url} className="min-h-0 w-full flex-1 border-0" />
        </Cartao>
      ) : null}
      </div>
    </div>
  );
}
